<template>
	<div v-if="campaign === null"
		 class="table-list">
		<div class="table-headlines">
			<div class="main">
				<div class="title">
					<span>{{ $t('google.ads.campaigns') }}</span>
				</div>
			</div>

			<div class="metrics">
				<div v-for="item in views[view]" class="metric">
					<i class="fa-fw" :class="item.icon" />
					<span>{{ item.label }}</span>
				</div>
			</div>
		</div>

		<div class="table-rows">
			<template v-if="items !== null"
					  v-for="(item, index) in filteredItems">
				<div @click="setCampaign(item)"
					 class="table-row">
					<div class="main">
						<div class="name">{{ item.campaign.name }}</div>

						<div class="status-list">
							<div v-if="item.campaign.status === 'ENABLED'" class="status success">
								<i class="fas fa-check fa-fw" />
								<span>{{ $t('google.ads.statuses.active') }}</span>
							</div>

							<div v-else-if="item.campaign.status === 'PAUSED'" class="status warning">
								<i class="fas fa-clock fa-fw" />
								<span>{{ $t('google.ads.statuses.paused') }}</span>
							</div>

							<div v-else-if="item.campaign.status === 'REMOVED'" class="status danger">
								<i class="fas fa-trash fa-fw" />
								<span>{{ $t('google.ads.statuses.removed') }}</span>
							</div>

							<div v-else class="status danger">
								<i class="fas fa-remove fa-fw" />
								<span>{{ $t('google.ads.statuses.inactive') }}</span>
							</div>
						</div>
					</div>

					<div class="metrics">
						<div v-for="viewItem in views[view]" class="metric">
							<div class="metric-label">
								<i class="fa-fw" :class="viewItem.icon" />
								<span>{{ viewItem.label }}</span>
							</div>

							<span v-if="formatValue(item, viewItem) !== null">{{ formatValue(item, viewItem) }}</span>
							<span v-else>-</span>
						</div>
					</div>
				</div>

				<template v-if="view === 'conversions' && resolveConversions(item.campaign.id).length > 0">
					<div @click="setCampaign(item)"
						 class="table-sub-headlines">
						<div class="main">
							<div class="title">
								<span>{{ $t('google.ads.conversions.label') }}</span>
							</div>
						</div>
					</div>

					<div @click="setCampaign(item)"
						 v-for="(conversionAction, index) in resolveConversions(item.campaign.id)"
						 :key="`conversion-action-${index}`"
						 class="table-sub-row">
						<div class="main">
							<div class="name">{{ conversionAction.segments.conversionActionName }}</div>
						</div>

						<div class="metrics">
							<div class="metric">
								<div class="metric-label">
									<i class="fa-fw far fa-fw far fa-exchange" />
									<span>{{ $t('google.ads.conversions.conversions') }}</span>
								</div>

								<span>{{ Math.round(conversionAction.metrics.conversions) }}</span>
							</div>

							<div class="metric">
								<div class="metric-label">
									<i class="fa-fw far fa-fw far fa-dollar" />
									<span>{{ $t('google.ads.metrics.costPerConversion') }}</span>
								</div>

								<span>{{ marketingFormat(item.metrics.costMicros / conversionAction.metrics.conversions / 1000000, 'currency', { currency: item.customer.currencyCode }) }}</span>
							</div>

							<div class="metric">
								<div class="metric-label">
									<i class="fa-fw far fa-fw far fa-percentage" />
									<span>{{ $t('google.ads.metrics.conversionRate') }}</span>
								</div>

								<span>{{ marketingFormat(Math.round(conversionAction.metrics.conversions) / Math.round(item.metrics.interactions) * 100, 'percentage') }}</span>
							</div>

							<div class="metric">
								<div class="metric-label">
									<i class="fa-fw far fa-dollar" />
									<span>{{ $t('google.ads.conversions.conversionsValue') }}</span>
								</div>

								<span>{{ marketingFormat(conversionAction.metrics.conversionsValue, 'currency', { currency: item.customer.currencyCode }) }}</span>
							</div>

							<div class="metric secondary">
								<div class="metric-label">
									<i class="fa-fw far fa-piggy-bank" />
									<span>{{ $t('google.ads.conversions.conversionsValue') }}</span>
								</div>

								<span>-</span>
							</div>
						</div>
					</div>
				</template>
			</template>

			<div v-if="items === null"
				 v-for="n in 8"
				 :key="`campaign-placeholder-${n}`"
				 class="table-row">
				<div class="mock main">
					<div class="name">Name here</div>
				</div>

				<div class="mock metrics">
					<div v-for="item in views[view]" class="metric">
						<div class="metric-label">
							<i class="fa-fw" :class="item.icon" />
							<span>{{ item.label }}</span>
						</div>

						<span>XYZ</span>
					</div>
				</div>
			</div>
		</div>
	</div>

	<ad-sets v-else
			 :network="network"
			 :networks="networks"
			 :views="views"
			 :status="status"
			 :view="view"
			 :query="query"
			 :campaign="campaign"
			 :ad-set="adSet"
			 :ad="ad"
			 @campaign="setCampaign"
			 @adSet="setAdSet"
			 @ad="setAd"
	/>
</template>

<style lang="scss" scoped>
div.table-list {
	display: flex;
	flex-direction: column;
	border: 1px solid #eee;
	border-radius: 8px;
	background-color: #fff;

	.main {
		width: 320px;
	}

	> div.table-headlines {
		display: flex;
		align-items: center;
		min-height: 60px;
		gap: 20px;
		border-bottom: 1px solid #eee;

		color: #1f1f1f;
		font-size: 16px;
		font-weight: 600;

		.controls {
			display: flex;
			align-items: center;
			gap: 6px;

			margin-left: auto;

			.search {
				position: relative;
				border: 1px solid #ccc;
				border-radius: 6px;
				overflow: hidden;

				> div.icon {
					display: flex;
					align-items: center;
					justify-content: center;
					padding: 0 0 0 8px;
					position: absolute;
					pointer-events: none;
					touch-action: none;
					top: 0;
					bottom: 0;
					left: 0;
					font-size: 16px;
				}

				input {
					height: 36px;
					border: 0;
					outline: 0;
					box-shadow: none;
					padding: 0 14px 0 36px;
					line-height: 100%;
					font-size: 14px;
					font-weight: 400;
				}
			}
		}

		> div.main {
			display: flex;
			align-items: center;
			gap: 8px;
			padding: 15px 25px;

			> div.title {
				> span {
					color: #9f9f9f;
					font-weight: 400;

					&:after {
						color: #9f9f9f;
						display: inline-block;
						padding: 0 7px;
						content: '/';
					}

					&:last-child {
						color: #000;
						font-weight: 500;

						&:after {
							display: none;
						}
					}
				}
			}
		}

		> div.metrics {
			display: flex;
			flex: 1;

			> div.metric {
				display: flex;
				flex-direction: column;
				align-items: center;
				justify-content: center;
				gap: 4px;

				padding: 15px 25px;
				min-height: 100%;

				flex: 1 1 0;
				width: 0;

				i {
					font-size: 20px;
				}

				font-size: 12px;
				font-weight: 500;
			}
		}
	}

	> div.table-rows {
		display: flex;
		flex-direction: column;

		> div.table-row {
			display: flex;
			gap: 20px;
			border-top: 1px solid #dcdcdc;

			&:first-child {
				border-top: 0;
			}

			&:nth-child(odd) {
				background-color: #fafafa;
			}

			&:hover {
				cursor: pointer;
				user-select: none;
				background-color: #eee;
			}

			.main {
				display: flex;
				flex-direction: column;
				align-items: flex-start;
				gap: 6px;
				padding: 15px 25px;

				.name {
					color: #000;
					font-size: 13px;
					font-weight: 500;
				}

				.status-list {
					display: flex;
					align-items: center;
					gap: 6px;

					> div.status {
						display: flex;
						align-items: center;
						gap: 4px;
						line-height: 100%;

						color: #383838;
						font-size: 11px;
						font-weight: 500;
						letter-spacing: -.1px;
						padding: 4px 5px;
						border-radius: 4px;
						background-color: #eee;

						i {
							margin-top: 1px;
						}

						&.success {
							color: #fff;
							background-color: #73af6e;
						}

						&.warning {
							color: #383838;
							background-color: #eee16e;
						}

						&.danger {
							color: #fff;
							background-color: #cc6f6f;
						}
					}
				}
			}

			> div.metrics {
				display: flex;
				flex: 1;

				> div.metric {
					flex: 1 1 0;
					width: 0;

					padding: 15px 25px;
					min-height: 100%;

					display: flex;
					align-items: center;
					justify-content: center;

					.metric-label {
						display: none;
					}

					&:nth-child(odd) {
						background-color: rgba(0, 0, 0, .02);
					}

					> span {
						padding: 4px 4px;
						line-height: 100%;
						font-size: 13px;
						font-weight: 500;
					}
				}
			}
		}

		> div.table-sub-headlines {
			display: flex;
			align-items: center;
			min-height: 50px;
			gap: 20px;

			border-top: 1px solid #eee;
			border-bottom: 1px solid #eee;

			color: #1f1f1f;
			font-size: 13px;
			font-weight: 600;

			background-color: #fff;

			> div.main {
				display: flex;
				align-items: center;
				gap: 8px;
				padding: 6px 25px 6px 60px;

				> div.title {
					> span {
						color: #000;
						font-weight: 500;
					}
				}
			}

			> div.metrics {
				display: flex;
				flex: 1;

				> div.metric {
					display: flex;
					flex-direction: row;
					align-items: center;
					justify-content: center;
					gap: 4px;

					padding: 6px 25px;
					min-height: 100%;

					flex: 1 1 0;
					width: 0;

					i {
						font-size: 14px;
					}

					font-size: 11px;
					font-weight: 500;
				}
			}
		}

		> div.table-sub-row {
			display: flex;
			gap: 20px;
			border-bottom: 1px solid #eee;

			&:nth-child(odd) {
				background-color: #fafafa;
			}

			&:hover {
				cursor: pointer;
				user-select: none;
				background-color: #eee;
			}

			.main {
				display: flex;
				flex-direction: column;
				align-items: flex-start;
				gap: 6px;
				padding: 15px 25px 15px 60px;

				.name {
					color: #565656;
					font-size: 13px;
					font-weight: 400;
				}

				.status-list {
					display: flex;
					align-items: center;
					gap: 6px;

					> div.status {
						display: flex;
						align-items: center;
						gap: 4px;
						line-height: 100%;

						color: #383838;
						font-size: 11px;
						font-weight: 500;
						letter-spacing: -.1px;
						padding: 4px 5px;
						border-radius: 4px;
						background-color: #eee;

						i {
							margin-top: 1px;
						}

						&.success {
							color: #fff;
							background-color: #73af6e;
						}

						&.warning {
							color: #383838;
							background-color: #eee16e;
						}

						&.danger {
							color: #fff;
							background-color: #cc6f6f;
						}
					}
				}
			}

			> div.metrics {
				display: flex;
				flex: 1;

				> div.metric {
					flex: 1 1 0;
					width: 0;

					padding: 15px 25px;
					min-height: 100%;

					display: flex;
					align-items: center;
					justify-content: center;

					.metric-label {
						display: none;
					}

					&:nth-child(odd) {
						background-color: rgba(0, 0, 0, .02);
					}

					> span {
						padding: 4px 4px;
						line-height: 100%;
						color: #656565;
						font-size: 13px;
						font-weight: 500;
					}

					&.secondary {
						> span {
							color: #a1a1a1;
							font-weight: 400;
						}
					}
				}
			}
		}
	}

	> div.table-pagination {
		padding: 25px;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 15px;

		> button {
			display: flex;
			align-items: center;
			justify-content: center;
			width: 35px;
			height: 35px;
			border-radius: 50%;
			color: #000;
			font-size: 15px;
			border: 2px solid #ccc;
			background-color: #fff;

			&:disabled {
				opacity: .5;
			}

			&:hover {
				color: #fff;
				border-color: #000;
				background-color: #000;
			}
		}
	}
}

@media(max-width: 750px) {
	div.table-list {
		.main {
			width: 100%;
		}

		> div.table-headlines {
			> div.main {
				padding: 15px !important;
			}

			> div.metrics {
				display: none;
			}
		}

		> div.table-rows {
			> div.table-row {
				display: flex;
				flex-direction: column;
				padding: 0 !important;

				> div.main {
					flex: 0;
					padding: 15px 15px 0 15px !important;
				}

				> div.metrics {
					flex: 0;

					display: flex;
					flex-direction: column;
					align-items: flex-start;

					> div.metric {
						display: flex;
						width: 100%;
						justify-content: space-between;
						border-bottom: 1px solid #eee;
						padding: 25px 15px !important;

						&:last-child {
							border-bottom: 0;
						}
					}

					.metric-label {
						display: flex !important;
						align-items: center;
						gap: 6px;

						font-weight: 500;

						i {
							color: #274fd2;
						}
					}
				}
			}

			> div.table-sub-headlines {
				> div.main {
					padding: 15px 15px 15px 40px !important;
				}

				> div.metrics {
					display: none;
				}
			}

			> div.table-sub-row {
				display: flex;
				flex-direction: column;
				padding: 0 !important;

				> div.main {
					flex: 0;
					padding: 15px 15px 0 40px !important;
				}

				> div.metrics {
					flex: 0;

					display: flex;
					flex-direction: column;
					align-items: flex-start;

					> div.metric {
						display: flex;
						width: 100%;
						justify-content: space-between;
						border-bottom: 1px solid #eee;
						padding: 25px 15px 25px 40px !important;

						&:last-child {
							border-bottom: 0;
						}
					}

					.metric-label {
						display: flex !important;
						align-items: center;
						gap: 6px;

						font-weight: 500;

						i {
							color: #274fd2;
						}
					}
				}
			}
		}
	}
}
</style>

<script>
const GoogleAdsService = require('@/services/google-ads/GoogleAdsService')

import datepickerMixin from '@/app/datepicker/datepicker.mixin'
import marketingMixin from '@/mixins/marketingMixin'

import moment from 'moment'
import AdSets from './AdSets'
import {mapGetters} from "vuex";

export default {
	mixins: [
		datepickerMixin,
		marketingMixin
	],

	props: {
		network: {
			type: String,
			required: false,
			default: 'google'
		},

		conversionActions: {
			type: Array,
			required: false,
			default: null
		},

		networks: {
			type: Object
		},

		views: {
			type: Object,
			required: true
		},

		status: {
			type: String,
			required: true
		},

		view: {
			type: String,
			required: true
		},

		campaign: {
			type: Object,
			required: false,
			default: null
		},

		adSet: {
			type: Object,
			required: false,
			default: null
		},

		ad: {
			type: Object,
			required: false,
			default: null
		},

		query: {
			type: String,
			required: false,
			default: ''
		}
	},

	data: () => ({
		items: null,
		conversions: null,
		page: 1,
		limit: 50,
		total: null
	}),

	computed: {
		...mapGetters("customer", {
			customer: "getCustomer",
		}),

		...mapGetters('ghost', {
			isDemo: 'isDemo',
		}),

		filteredItems() {
			const query = String(this.query || '').toLowerCase()

			if (query.length === 0) {
				return this.items
			}

			return this.items.filter(item => String(item.campaign.name || '').toLowerCase().includes(query))
		},

		integrationId() {
			if (this.isDemo) {
				return 'demo-id'
			}

			const integrations = this.customer.integrations

			const match = integrations.find(integration => integration.service.name === "Google Ads")

			return !! match
				? match.id
				: null
		},

		orderBy() {
			return this.views[this.view].reduce(
				(carry, item) => {
					if (!! carry || item.source !== 'metrics') {
						return carry
					}

					return `metrics.${item.field} DESC`
				},
				null
			)
		}
	},

	watch: {
		from() {
			this.reset()
			this.onLoad()
		},

		status() {
			this.reset()
			this.onLoad()
		},

		network() {
			this.onLoad()
		},

		orderBy() {
			this.onLoad()
		}
	},

	mounted() {
		this.onLoad()
	},

	methods: {
		reset() {
			this.page = 1
			this.total = null
			this.items = null
		},

		onLoad() {
			this.items = null

			GoogleAdsService.fetchCampaigns(
				this.from,
				this.to,
				this.integrationId,
				(response) => {
					this.items = Array.isArray(response.data)
						? response.data
						: []
				},
				(res) => {
					// @TODO: Error
				},
				this.status === 'active'
					? ['ENABLED']
					: ['PAUSED', 'REMOVED'],
				this.limit,
				this.page,
				this.networks[this.network].networks,
				this.orderBy
			)

			GoogleAdsService.fetchCampaignsConversions(
				this.from,
				this.to,
				this.integrationId,
				(response) => {
					this.conversions = Array.isArray(response.data)
						? response.data
						: []
				},
				(response) => {
					// @TODO: Error
				},
				this.status === 'active'
					? ['ENABLED']
					: ['PAUSED', 'REMOVED'],
				this.limit,
				this.page,
				this.networks[this.network].networks
			)
		},

		formatValue(item, metric) {
			let value = null

			if (metric.source === 'metrics') {
				value = item.metrics[metric.field]
			} else if (metric.source === 'calculation') {
				value = metric.calculation(item.metrics, item)
			}

			if (! value) {
				return null
			}

			const type = metric.type

			switch (type) {
				case 'currency':
					return this.marketingFormat(
						Number(value) / 1000000,
						type,
						{ currency: item.customer.currencyCode }
					)

				default:
					return this.marketingFormat(
						value,
						type
					)
			}
		},

		setCampaign(campaign) {
			this.$emit('campaign', campaign)
		},

		setAdSet(adSet) {
			this.$emit('adSet', adSet)
		},

		setAd(ad) {
			this.$emit('ad', ad)
		},

		formatDate(date) {
			return moment(date).format('ll')
		},

		resolveConversions(id) {
			return (this.conversions || []).filter(
				item => item.campaign.id === id
			)
		},

		hasDate(date) {
			const oneYear = moment().add(1, 'year')

			return !! date && moment(date).isBefore(oneYear)
		}
	},

	components: {
		AdSets
	},
}
</script>
